import type { ChecklistEntry, ReviewChecklistProduct } from './checklist';

export type CardLookupField = 'number' | 'name' | 'category' | 'year' | 'brand' | 'product';
export interface CardLookupRequest {
  fields: Partial<Record<CardLookupField, string>>;
  requiredFields?: CardLookupField[];
  limit?: number;
  /** Revalidate canonical source and the original confirmed search criteria before applying. */
  validateResultId?: string;
}
export interface CardLookupResponse {
  items: Array<{
    resultId: string;
    checklist: ReviewChecklistProduct;
    entry: ChecklistEntry;
    matchDetails: Array<{
      field: CardLookupField;
      status: 'exact' | 'similar' | 'different' | 'missing';
      explanation: string;
    }>;
  }>;
  coverage: {
    partial: boolean;
    reason?:
      | 'search-budget'
      | 'result-limit'
      | 'unavailable-source'
      | 'index-incomplete'
      | 'number-format';
    evaluatedEntries: number;
  };
  needsMoreDetail?: string;
}

export const CARD_LOOKUP_VERSION = '1';
export const CARD_LOOKUP_PREFIX = `CARD_LOOKUP#${CARD_LOOKUP_VERSION}#`;
export const CARD_LOOKUP_META_PK = `${CARD_LOOKUP_PREFIX}META`;
export const CARD_LOOKUP_FIELDS: readonly CardLookupField[] = [
  'number',
  'name',
  'category',
  'year',
  'brand',
  'product',
];

export function cardLookupText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}
/** Number punctuation/spacing is folded, but significant prefixes and leading zeros remain. */
export function cardLookupNumber(value: string): string {
  return cardLookupText(value).replace(/ /g, '');
}
export function cardLookupNameTokens(value: string): string[] {
  return [
    ...new Set(
      cardLookupText(value)
        .split(' ')
        .filter((token) => token.length >= 3),
    ),
  ].slice(0, 12);
}
export interface CardLookupPointer {
  pk: string;
  sk: string;
  recordType: 'CARD_LOOKUP_POINTER';
  checklistId: string;
  checklistSk: string;
  fingerprint: string;
}
/** Small rebuildable pointers, never copies of authoritative identity. */
export function projectCardLookupPointers(row: Record<string, unknown>): CardLookupPointer[] {
  const identity = row['identity'] as { number?: string; name?: string } | undefined;
  const source = row['sourceOwnership'] as { snapshotFingerprint?: string } | undefined;
  if (
    typeof row['pk'] !== 'string' ||
    !row['pk'].startsWith('SOURCE#') ||
    typeof row['sk'] !== 'string' ||
    !identity ||
    !source?.snapshotFingerprint
  )
    return [];
  const number = cardLookupNumber(identity.number ?? '');
  const suffix = [row['pk'], source.snapshotFingerprint, row['sk']]
    .map(encodeURIComponent)
    .join('#');
  const base = {
    recordType: 'CARD_LOOKUP_POINTER' as const,
    checklistId: row['pk'],
    checklistSk: row['sk'],
    fingerprint: source.snapshotFingerprint,
  };
  const items: CardLookupPointer[] = number
    ? [{ ...base, pk: `${CARD_LOOKUP_PREFIX}N#${number}`, sk: suffix }]
    : [];
  for (const token of cardLookupNameTokens(identity.name ?? '')) {
    items.push({
      ...base,
      pk: `${CARD_LOOKUP_PREFIX}T#${token.slice(0, 3)}`,
      sk: `${token}#${suffix}`,
    });
    if (number)
      items.push({
        ...base,
        pk: `${CARD_LOOKUP_PREFIX}NT#${number}#${token.slice(0, 3)}`,
        sk: `${token}#${suffix}`,
      });
  }
  return items;
}
export function cardLookupAnchorQueries(
  fields: CardLookupRequest['fields'],
): Array<{ pk: string; prefix?: string }> {
  const number = cardLookupNumber(fields.number ?? '');
  const tokens = cardLookupNameTokens(fields.name ?? '').sort(
    (a, b) => b.length - a.length || a.localeCompare(b),
  );
  const combined = number
    ? tokens.map((token) => ({
        pk: `${CARD_LOOKUP_PREFIX}NT#${number}#${token.slice(0, 3)}`,
        prefix: token,
      }))
    : [];
  return [
    ...combined,
    ...(number ? [{ pk: `${CARD_LOOKUP_PREFIX}N#${number}` }] : []),
    ...tokens.map((token) => ({
      pk: `${CARD_LOOKUP_PREFIX}T#${token.slice(0, 3)}`,
      prefix: token,
    })),
  ];
}
/** Fields explicitly confirmed by a reviewer; hints never count toward the minimum. */
export function cardLookupConfirmedFields(request: CardLookupRequest): CardLookupField[] {
  return CARD_LOOKUP_FIELDS.filter(
    (field) =>
      request.requiredFields?.includes(field) && cardLookupText(request.fields[field] ?? ''),
  );
}

export function cardLookupHasMinimumConfirmedFields(request: CardLookupRequest): boolean {
  const confirmed = cardLookupConfirmedFields(request);
  return ['number', 'category', 'year', 'brand'].every((field) =>
    confirmed.includes(field as CardLookupField),
  );
}

function seasonYears(value: string): number[] {
  const match = value.trim().match(/^(\d{4})(?:\s*[-–—/]\s*(\d{2}|\d{4}))?$/);
  if (!match) return [];
  const start = Number(match[1]);
  if (!match[2]) return [start];
  let end = Number(match[2]);
  if (match[2].length === 2) {
    end += Math.floor(start / 100) * 100;
    if (end < start) end += 100;
  }
  // Interpret actual adjacent seasons, never arbitrary numeric substrings or wide ranges.
  return end === start + 1 ? [start, end] : [];
}

function normalizedFullName(value: string): string {
  return cardLookupText(value.replace(/[.'’]/g, ''));
}

/** Existing pointers split punctuation before indexing. Broaden name-only retrieval to
 * bounded shard queries so normalization cannot exclude a valid full-name match. */
export function cardLookupRefinementAnchorQueries(
  request: CardLookupRequest,
): Array<{ pk: string; prefix?: string }> {
  const fields = Object.fromEntries(
    cardLookupConfirmedFields(request).map((field) => [field, request.fields[field]]),
  );
  const anchors = cardLookupAnchorQueries(fields);
  // The exact number partition already includes every name spelling for that number.
  if (fields.number || !fields.name) return anchors;
  const shards = new Set<string>();
  for (const token of cardLookupNameTokens(normalizedFullName(fields.name))) {
    // Every indexed punctuation-separated token is at least three characters. It may
    // start inside the normalized query: D'Andre -> andre, Farfetch'd -> farfetch.
    for (let offset = 0; offset <= token.length - 3; offset++)
      shards.add(token.slice(offset, offset + 3));
  }
  return [...anchors, ...[...shards].map((shard) => ({ pk: `${CARD_LOOKUP_PREFIX}T#${shard}` }))];
}

/** Strict eligibility is field-specific; similarity is only useful for ranking hints. */
export function cardLookupFieldMatches(
  field: CardLookupField,
  clue: string,
  checklist: ReviewChecklistProduct,
  entry?: ChecklistEntry,
): boolean {
  if (field === 'number') {
    const query = cardLookupNumber(clue);
    return !!query && query === cardLookupNumber(entry?.identity.number ?? '');
  }
  if (field === 'name') {
    const query = normalizedFullName(clue);
    return !!query && query === normalizedFullName(entry?.identity.name ?? '');
  }
  if (field === 'category') {
    const query = cardLookupText(clue);
    return !!query && query === cardLookupText(checklist.category);
  }
  if (field === 'year') {
    const query = seasonYears(clue);
    const actual = seasonYears(checklist.product.year);
    return query.length > 0 && query.every((year) => actual.includes(year));
  }
  const phrase = cardLookupText(clue);
  if (!phrase) return false;
  // A brand may be recorded in the product label; do not manufacture phrases across labels.
  return [checklist.product.brand, checklist.product.product].some((label) =>
    ` ${cardLookupText(label)} `.includes(` ${phrase} `),
  );
}

function oneEditApart(left: string, right: string): boolean {
  if (Math.abs(left.length - right.length) > 1) return false;
  let i = 0,
    j = 0,
    edits = 0;
  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (left.length >= right.length) i++;
    if (right.length >= left.length) j++;
  }
  return edits + Number(i < left.length || j < right.length) <= 1;
}
export function rankCardLookupItem(
  request: CardLookupRequest,
  checklist: ReviewChecklistProduct,
  entry: ChecklistEntry,
): { item: CardLookupResponse['items'][number]; score: number } | undefined {
  const values: Record<CardLookupField, string> = {
    number: entry.identity.number,
    name: entry.identity.name,
    category: checklist.category,
    ...checklist.product,
  };
  const weights: Record<CardLookupField, number> = {
    number: 12,
    name: 10,
    category: 4,
    year: 4,
    brand: 3,
    product: 6,
  };
  let score = 0;
  const details: CardLookupResponse['items'][number]['matchDetails'] = [];
  for (const field of CARD_LOOKUP_FIELDS) {
    const clue = request.fields[field]?.trim();
    if (!clue) continue;
    const normalize = field === 'number' ? cardLookupNumber : cardLookupText;
    const query = normalize(clue),
      actual = normalize(values[field] ?? '');
    let status: CardLookupResponse['items'][number]['matchDetails'][number]['status'] =
      cardLookupFieldMatches(field, clue, checklist, entry)
        ? 'exact'
        : actual === ''
          ? 'missing'
          : 'different';
    const queryTokens = query.split(' '),
      actualTokens = actual.split(' ');
    const tokenMatch = queryTokens.every((token) => actualTokens.includes(token));
    if (
      field !== 'number' &&
      field !== 'year' &&
      status === 'different' &&
      (tokenMatch ||
        queryTokens.every((token) =>
          actualTokens.some(
            (other) =>
              (token.length >= 3 && other.startsWith(token)) ||
              (token.length >= 4 && oneEditApart(token, other)),
          ),
        ))
    )
      status = 'similar';
    if (
      request.requiredFields?.includes(field) &&
      !cardLookupFieldMatches(field, clue, checklist, entry)
    )
      return undefined;
    score +=
      status === 'exact'
        ? weights[field] * 2
        : status === 'similar'
          ? weights[field]
          : -weights[field];
    details.push({
      field,
      status,
      explanation:
        status === 'exact'
          ? `${field} matches`
          : status === 'similar'
            ? `${field} is similar: ${values[field]}`
            : status === 'missing'
              ? `${field} is unavailable in this checklist`
              : `${field} differs: ${values[field]}`,
    });
  }
  if (!details.some((detail) => detail.status === 'exact' || detail.status === 'similar'))
    return undefined;
  const resultId = [
    checklist.checklistId,
    entry.source?.snapshotFingerprint ?? '',
    entry.source?.sourceItemId ?? JSON.stringify(entry.key),
  ]
    .map(encodeURIComponent)
    .join('/');
  return { item: { resultId, checklist, entry, matchDetails: details }, score };
}
